import { kebabCase } from "lodash";
import { GetServerSideProps } from "next";

import config from "~/config";
import { getTfSimpleVaspInfoForServerSideRender, getTfVaspListForServerSideRender } from "~/endpoints/trustFramework";
import NotFoundPage from "~/pages/404";
import NetworkVaspPage from "~/templates/network/NetworkVaspPage";
import { VASP } from "~/types/vaspid";
import auth0 from "~/utils/auth0";

// This function gets called at each request time.
export const getServerSideProps: GetServerSideProps = async (context) => {
  const { req, res, params } = context;

  if (!config.auth0.audience) {
    //this env vars are not available at CI/CD build time, and will throw error
    return {
      props: {
        error: false,
        previousVaspURL: `dummyURL`,
        nextVaspURL: `dummyURL`,
        vasp: "dummyVASP",
        isProductionEnv: false,
      },
    };
  }

  const isProductionEnv = process.env.ENVIRONMENT === "production";

  try {
    const currentVaspDid = params?.did as string;
    const accessToken = auth0.getSession(req, res)?.accessToken;

    const currentVaspInfo = await getTfSimpleVaspInfoForServerSideRender(currentVaspDid, accessToken);
    const vaspDatas = await getTfVaspListForServerSideRender();

    const currenVaspIndex = vaspDatas.findIndex((vasp) => vasp.did === currentVaspDid);

    // Handle unchecked VASPs (won't exist within the nextVasp/prevVasp flow)
    if (currenVaspIndex === -1) {
      return {
        props: { error: false, vasp: currentVaspInfo, isProductionEnv },
      };
    }

    //first and last vasp link to each other
    const previousVasp = currenVaspIndex === 0 ? vaspDatas[vaspDatas.length - 1] : vaspDatas[currenVaspIndex - 1];
    const nextVasp = currenVaspIndex === vaspDatas.length - 1 ? vaspDatas[0] : vaspDatas[currenVaspIndex + 1];

    return {
      props: {
        error: false,
        previousVaspURL: `/network/${previousVasp.did}/${kebabCase(previousVasp.name)}`,
        nextVaspURL: `/network/${nextVasp.did}/${kebabCase(nextVasp.name)}`,
        vasp: currentVaspInfo,
        isProductionEnv,
      },
    };
  } catch (e) {
    return {
      props: {
        error: true, //we only care about the error
        previousVaspURL: `dummyURL`,
        nextVaspURL: `dummyURL`,
        vasp: "dummyVASP",
        isProductionEnv,
      },
    };
  }
};

type CurrentPageProps = {
  nextVaspURL: string;
  previousVaspURL: string;
  vasp: VASP;
  error?: boolean;
  isProductionEnv: boolean;
};

export default function CurrentPage({ nextVaspURL, previousVaspURL, vasp, error, isProductionEnv }: CurrentPageProps) {
  if (error) {
    return <NotFoundPage status={404} title={`VASP Not Found`} />;
  }
  return (
    <NetworkVaspPage
      nextVaspURL={nextVaspURL}
      previousVaspURL={previousVaspURL}
      vasp={vasp}
      isProductionEnv={isProductionEnv}
    />
  );
}
